/**
 * Shared portfolio data for /work and /work/$slug.
 * Case-study fields are optional so cards can render with core meta only.
 */

export type Category =
  | "همه"
  | "طراحی سایت"
  | "سئو و محتوا"
  | "افزایش فروش"
  | "برندینگ"
  | "بازاریابی"
  | "تکنولوژی و اتوماسیون";

export const CATEGORIES: Category[] = [
  "همه",
  "طراحی سایت",
  "سئو و محتوا",
  "افزایش فروش",
  "برندینگ",
  "بازاریابی",
  "تکنولوژی و اتوماسیون",
];

export type CoverKind =
  "browser" | "mobile" | "search" | "funnel" | "identity" | "workflow" | "content" | "campaign";

export type CaseStudy = {
  summary: string;
  context: string;
  challengeLead: string;
  challengeBody: string;
  findings: { title: string; body: string }[];
  strategy: { problem: string; decision: string; solution: string }[];
  execution: { title: string; body: string; kind?: CoverKind }[];
  results: {
    qualitative: string[];
    metrics?: { value: string; label: string; period?: string; source?: string }[];
  };
  learnings?: string[];
  serviceLinks: { label: string; to: string }[];
  siteUrl?: string;
  projectType?: string;
  duration?: string;
};

export type Project = {
  slug: string;
  name: string;
  industry: string;
  year: string;
  category: Exclude<Category, "همه">;
  challenge: string;
  services: string[];
  outcome: string;
  cover: CoverKind;
  size?: "large" | "wide" | "standard";
  caseStudy?: CaseStudy;
};

export const FEATURED: Project = {
  slug: "atlas-industrial-web",
  name: "گروه صنعتی اطلس",
  industry: "تولید و صنایع B2B",
  year: "۱۴۰۳",
  category: "طراحی سایت",
  challenge:
    "سایت قدیمی نه معماری روشنی داشت، نه مسیر مشخصی برای درخواست پیش‌فاکتور. تیم فروش، منبع سرنخ قابل اتکایی از وب نداشت.",
  services: ["استراتژی دیجیتال", "طراحی UX/UI", "توسعه", "سئوی فنی"],
  outcome:
    "بازطراحی معماری تجربه و ساخت یک زیرساخت دیجیتال قابل توسعه که مسیر روشن از بازدید تا درخواست پیش‌فاکتور را در سایت جا انداخت.",
  cover: "browser",
  caseStudy: {
    projectType: "بازطراحی وب‌سایت سازمانی",
    summary:
      "بازطراحی تجربه‌ی دیجیتال گروه صنعتی اطلس با هدف تبدیل سایت از یک بروشور ایستا به یک زیرساخت فروش قابل اندازه‌گیری برای تیم B2B.",
    context:
      "اطلس یک هلدینگ تولیدی با چند خط محصول صنعتی و مشتریان سازمانی است. سایت قبلی طی چند سال به‌صورت تدریجی رشد کرده بود؛ ساختار محصولات پیچیده، مسیر تماس مبهم و تیم فروش عملاً از سایت به‌عنوان کانال ورودی سرنخ استفاده نمی‌کرد.",
    challengeLead:
      "سایت هیچ منطق روشنی برای هدایت مخاطب از معرفی محصول تا درخواست پیش‌فاکتور نداشت.",
    challengeBody:
      "کاربران در پیدا کردن دسته‌بندی محصول درست سردرگم می‌شدند، صفحات محصول اطلاعات فنی کافی نداشتند و فرم تماس عمومی، تیم فروش را قادر به اولویت‌بندی سرنخ‌ها نمی‌کرد. نتیجه: بیشتر فروش‌های واقعی از کانال‌های آفلاین می‌آمد و سایت هزینه‌ای بدون بازگشت قابل اندازه‌گیری بود.",
    findings: [
      {
        title: "معماری اطلاعات ناهم‌راستا با ذهن خریدار",
        body: "دسته‌بندی محصولات بر اساس ساختار داخلی شرکت طراحی شده بود، نه بر اساس کاربرد در صنایع مشتری.",
      },
      {
        title: "صفحه‌ی محصول بدون تصمیم‌ساز",
        body: "اطلاعات فنی کلیدی مانند مشخصات، کاربردها و مدارک پشتیبان در PDF‌های پراکنده پنهان شده بودند.",
      },
      {
        title: "مسیر تماس تک‌شکل",
        body: "تنها یک فرم عمومی وجود داشت که بین درخواست قیمت، درخواست کاتالوگ و سؤالات فنی تفکیک نمی‌کرد.",
      },
      {
        title: "زیرساخت سئوی تخریب‌شده",
        body: "URLها، متاداده‌ها و ساختار هدینگ‌ها با معماری واقعی محتوای جدید هم‌راستا نبودند.",
      },
    ],
    strategy: [
      {
        problem: "خریدار محصول درست را پیدا نمی‌کند.",
        decision: "بازتعریف معماری بر اساس صنعت و کاربرد.",
        solution: "دو مسیر ورود موازی: بر اساس دسته‌ی محصول و بر اساس صنعت مصرف‌کننده.",
      },
      {
        problem: "صفحه‌ی محصول تصمیم‌ساز نیست.",
        decision: "طراحی الگوی واحد صفحه‌ی محصول با ساختار مشخص.",
        solution: "بلاک‌های ثابت: مشخصات، کاربرد، مدارک، محصولات مرتبط، درخواست پیش‌فاکتور.",
      },
      {
        problem: "سرنخ‌ها قابل اولویت‌بندی نیستند.",
        decision: "تفکیک فرم‌ها بر اساس نیت مخاطب.",
        solution: "سه فرم مجزا: پیش‌فاکتور، مشاوره‌ی فنی و درخواست کاتالوگ، با فیلدهای متفاوت.",
      },
    ],
    execution: [
      {
        title: "معماری اطلاعات جدید",
        body: "بازتعریف ساختار سایت با دو نقطه‌ی ورود موازی و صفحه‌ی هاب برای هر صنعت.",
        kind: "content",
      },
      {
        title: "طراحی صفحه‌ی محصول",
        body: "الگوی واحد صفحه‌ی محصول با بلاک‌های ثابت و کامپوننت‌های قابل استفاده در سراسر کاتالوگ.",
        kind: "browser",
      },
      {
        title: "نسخه‌ی موبایل",
        body: "بازطراحی موبایل‌فرست با تمرکز روی دسترسی سریع به مشخصات فنی و دکمه‌ی درخواست.",
        kind: "mobile",
      },
      {
        title: "سئوی فنی و URL",
        body: "بازتعریف URLها، متاداده‌ها و ساختار breadcrumb هم‌راستا با معماری جدید.",
        kind: "search",
      },
    ],
    results: {
      qualitative: [
        "مسیر روشن از بازدید تا درخواست پیش‌فاکتور در سایت جا افتاد.",
        "تیم فروش، سایت را به‌عنوان یک کانال ورودی قابل اتکا در گردش کار خود جای داد.",
        "سرنخ‌ها با تفکیک نیت، قابل اولویت‌بندی و پیگیری منظم شدند.",
        "زیرساخت محتوا برای افزودن خطوط محصول جدید بدون بازطراحی، آماده شد.",
      ],
    },
    learnings: [
      "در B2B، معماری اطلاعات مهم‌تر از زیبایی صفحه است؛ خریدار سازمانی به‌دنبال قطعیت اطلاعاتی است، نه جلوه‌ی بصری.",
      "تفکیک فرم‌های تماس، مؤثرترین اهرم برای بهبود کیفیت سرنخ در سایت‌های صنعتی است.",
      "الگوی واحد صفحه‌ی محصول، هزینه‌ی نگهداری و توسعه‌ی کاتالوگ را در بلندمدت به‌شدت کاهش می‌دهد.",
    ],
    serviceLinks: [
      { label: "طراحی سایت", to: "/services/web-design" },
      { label: "سئو", to: "/services/seo" },
      { label: "مشاوره کسب‌وکار", to: "/services/business-consulting" },
    ],
  },
};

export const PROJECTS: Project[] = [
  {
    slug: "roshan-organic-growth",
    name: "روشن مارکت",
    industry: "خرده‌فروشی آنلاین",
    year: "۱۴۰۳",
    category: "سئو و محتوا",
    challenge: "ترافیک ارگانیک راکد و وابستگی کامل به تبلیغات پولی.",
    services: ["استراتژی سئو", "خوشه‌ی محتوا", "سئوی فنی"],
    outcome: "ساخت اقتدار موضوعی روی سه دسته‌ی اصلی و کاهش وابستگی به کمپین‌های پولی.",
    cover: "search",
    size: "large",
    caseStudy: {
      projectType: "استراتژی سئو و محتوا",
      summary:
        "طراحی یک سیستم رشد ارگانیک برای روشن مارکت با هدف کاهش وابستگی به تبلیغات پولی و ساخت اقتدار موضوعی پایدار.",
      context:
        "روشن مارکت یک خرده‌فروش آنلاین با چند دسته‌ی محصولی است. تا پیش از پروژه، تقریباً کل ترافیک از کمپین‌های پولی می‌آمد و توقف کمپین، به‌معنای توقف فروش بود.",
      challengeLead: "ترافیک ارگانیک راکد بود و برند در نتایج جست‌وجو حضور معناداری نداشت.",
      challengeBody:
        "محتوا به‌صورت پراکنده تولید می‌شد، ساختار دسته‌ها هم‌راستای نیت جست‌وجو نبود و صفحات محصول از منظر سئو خالی بودند. هزینه‌ی جذب مشتری در حال رشد و حاشیه‌ی سود در حال کاهش بود.",
      findings: [
        {
          title: "پوشش کلمات کلیدی سطحی",
          body: "برند فقط برای کلمات برندی رتبه داشت و در کلمات دسته‌ای غایب بود.",
        },
        {
          title: "معماری دسته‌ها بدون منطق سئویی",
          body: "URLها و ساختار دسته‌ها با نیت جست‌وجوی کاربر تطابق نداشت.",
        },
        {
          title: "محتوا بدون ستون",
          body: "مقالات پراکنده، بدون خوشه، بدون لینک‌سازی داخلی معنادار.",
        },
        {
          title: "کمبود صفحات ورود",
          body: "برای بسیاری از نیت‌های تجاری، صفحه‌ی هدف اختصاصی وجود نداشت.",
        },
      ],
      strategy: [
        {
          problem: "پوشش کلیدواژه‌ی محدود.",
          decision: "تعریف سه خوشه‌ی موضوعی اصلی.",
          solution: "نقشه‌ی خوشه با ستون + زیرموضوع + صفحات پشتیبان.",
        },
        {
          problem: "دسته‌ها هم‌راستا با جست‌وجو نیستند.",
          decision: "بازتعریف معماری دسته.",
          solution: "URL جدید، breadcrumb و لینک داخلی هم‌راستا با نیت.",
        },
        {
          problem: "صفحات ورود کافی نیست.",
          decision: "ساخت صفحات هاب برای هر خوشه.",
          solution: "الگوی واحد صفحه‌ی هاب با محتوا، فیلتر و لینک‌سازی.",
        },
      ],
      execution: [
        {
          title: "ممیزی و نقشه‌ی خوشه‌ی محتوا",
          body: "شناسایی سه خوشه‌ی موضوعی اصلی و طراحی نقشه‌ی محتوایی سه‌لایه.",
          kind: "content",
        },
        {
          title: "بازسازی معماری دسته‌ها",
          body: "بازنویسی URLها، متاداده‌ها و ساختار hreflang/breadcrumb.",
          kind: "search",
        },
        {
          title: "الگوی صفحه‌ی هاب",
          body: "طراحی الگوی صفحه‌ی هاب هر خوشه با ساختار محتوایی روشن و لینک‌سازی داخلی.",
          kind: "browser",
        },
      ],
      results: {
        qualitative: [
          "برند در سه خوشه‌ی موضوعی اصلی صاحب اقتدار موضوعی روشنی شد.",
          "وابستگی به کمپین‌های پولی برای دسته‌های اصلی به‌طور محسوس کاهش یافت.",
          "زیرساخت محتوایی برای رشد در دسته‌های جدید، بدون بازطراحی، آماده شد.",
        ],
      },
      learnings: [
        "سئو بدون معماری اطلاعات، تولید محتوای بی‌هدف است.",
        "اقتدار موضوعی از عمق پوشش یک خوشه ساخته می‌شود، نه از تعداد مقالات پراکنده.",
      ],
      serviceLinks: [
        { label: "سئو", to: "/services/seo" },
        { label: "تولید محتوا", to: "/services/content-production" },
      ],
    },
  },
  {
    slug: "meridian-sales-funnel",
    name: "مریدین B2B",
    industry: "خدمات سازمانی",
    year: "۱۴۰۳",
    category: "افزایش فروش",
    challenge: "سرنخ‌های ورودی زیاد، اما نرخ تبدیل پایین در مذاکره‌ی فروش.",
    services: ["طراحی قیف فروش", "CRM", "لندینگ‌های تخصصی"],
    outcome: "طراحی مسیر مشتری از اولین کلیک تا امضای قرارداد به‌صورت یک سیستم قابل اندازه‌گیری.",
    cover: "funnel",
    size: "large",
    caseStudy: {
      projectType: "طراحی سیستم فروش",
      summary:
        "بازطراحی قیف فروش مریدین به‌عنوان یک سیستم قابل اندازه‌گیری، از اولین کلیک تا امضای قرارداد.",
      context:
        "مریدین یک ارائه‌دهنده‌ی خدمات سازمانی است که در سال‌های اخیر ورودی سرنخ خوبی از کانال‌های مختلف داشت، اما بخش زیادی از سرنخ‌ها بعد از تماس اولیه در ادامه‌ی مذاکره از بین می‌رفت.",
      challengeLead: "قیف فروش وجود داشت، اما تعریف‌شده و قابل اندازه‌گیری نبود.",
      challengeBody:
        "مراحل قیف روشن نبود، صلاحیت‌سنجی سرنخ به تجربه‌ی شخصی هر کارشناس فروش وابسته بود، و CRM بیشتر برای ثبت اطلاعات استفاده می‌شد تا هدایت گفت‌وگو. نتیجه: نرخ تبدیل ناپایدار و غیرقابل پیش‌بینی.",
      findings: [
        {
          title: "نبود تعریف مشترک از سرنخ واجد شرایط",
          body: "هر کارشناس فروش تعریف خودش را داشت و اولویت‌ها متفاوت بود.",
        },
        {
          title: "لندینگ‌های عمومی",
          body: "همه‌ی کانال‌ها به یک صفحه‌ی عمومی می‌رسیدند، بدون تنظیم پیام برای هر بازار.",
        },
        {
          title: "CRM بدون منطق مرحله‌ای",
          body: "قیف در CRM به‌شکل واقعی وضعیت مذاکره را منعکس نمی‌کرد.",
        },
        {
          title: "پیگیری وابسته به حافظه",
          body: "پیگیری‌ها بر اساس یادآوری فردی کارشناسان انجام می‌شد.",
        },
      ],
      strategy: [
        {
          problem: "سرنخ‌ها هم‌سطح نیستند.",
          decision: "تعریف چارچوب صلاحیت‌سنجی مشترک.",
          solution: "معیارهای روشن BANT-مانند برای عبور سرنخ از هر مرحله.",
        },
        {
          problem: "پیام برای همه یکی است.",
          decision: "ساخت لندینگ‌های تخصصی.",
          solution: "چند لندینگ مبتنی بر صنعت و نوع خدمت با پیام و شواهد اختصاصی.",
        },
        {
          problem: "قیف در CRM واقعی نیست.",
          decision: "بازتعریف مراحل قیف در CRM.",
          solution: "مراحل روشن با معیار عبور و گزارش نرخ افت بین هر مرحله.",
        },
      ],
      execution: [
        {
          title: "قیف فروش جدید",
          body: "مراحل تعریف‌شده از بازدید تا امضا با شرط عبور مشخص در هر مرحله.",
          kind: "funnel",
        },
        {
          title: "لندینگ‌های تخصصی",
          body: "چند صفحه‌ی هدف بر اساس صنعت مخاطب، با پیام و فرم اختصاصی.",
          kind: "browser",
        },
        {
          title: "CRM و اتوماسیون پیگیری",
          body: "پیکربندی CRM، تعریف تسک‌های خودکار و یادآوری‌های پیگیری.",
          kind: "workflow",
        },
      ],
      results: {
        qualitative: [
          "قیف فروش برای اولین بار به‌صورت قابل اندازه‌گیری در گزارش‌های هفتگی دیده می‌شود.",
          "کیفیت گفت‌وگوی فروش با ورود سرنخ‌های واجد شرایط، به‌طور محسوس بهبود یافت.",
          "پیگیری‌ها از حالت شخصی خارج و به یک فرآیند تیمی تبدیل شدند.",
        ],
      },
      learnings: [
        "مشکل فروش، اغلب مشکل تعریف است، نه مشکل تلاش.",
        "بدون تعریف مشترک از سرنخ واجد شرایط، هر بهینه‌سازی قیف، تخمینی است.",
      ],
      serviceLinks: [
        { label: "افزایش فروش", to: "/services/sales-growth" },
        { label: "اتوماسیون", to: "/services/automation" },
      ],
    },
  },
  {
    slug: "nima-clinic-brand",
    name: "کلینیک نیما",
    industry: "سلامت و درمان",
    year: "۱۴۰۳",
    category: "برندینگ",
    challenge: "هویت پراکنده در نقاط تماس مختلف و نبود زبان بصری منسجم.",
    services: ["هویت بصری", "سیستم برند", "کاربرد در نقاط تماس"],
    outcome: "تعریف زبان بصری واحد و ساخت راهنمای برند برای همه‌ی نقاط تماس کلینیک.",
    cover: "identity",
    size: "wide",
    caseStudy: {
      projectType: "طراحی هویت بصری",
      summary: "بازتعریف هویت بصری کلینیک نیما به‌عنوان یک سیستم قابل توسعه، نه یک لوگو.",
      context:
        "کلینیک نیما در چند شعبه فعالیت می‌کند و طی سال‌ها، هویت بصری در نقاط تماس مختلف به‌صورت پراکنده رشد کرده بود؛ از تابلوی سردر تا کارت ویزیت پزشک و صفحه‌ی اینستاگرام، هر جا زبان بصری متفاوتی دیده می‌شد.",
      challengeLead: "برند در ذهن مخاطب یک تصویر واحد نمی‌ساخت.",
      challengeBody:
        "پراکندگی بصری باعث کاهش حس اعتماد در نقاط تماس اولیه می‌شد و توسعه‌ی مواد ارتباطی جدید، هر بار نیازمند تصمیم‌های سلیقه‌ای بود.",
      findings: [
        { title: "نبود سیستم رنگ", body: "پالت رنگی در کاربردهای مختلف متفاوت بود." },
        { title: "لوگو بدون قواعد کاربرد", body: "اندازه، فاصله و کاربردهای مجاز تعریف نشده بود." },
        { title: "تایپوگرافی متغیر", body: "در هر نقطه‌ی تماس، فونت متفاوتی استفاده می‌شد." },
        {
          title: "نبود الگوی نقاط تماس",
          body: "کارت ویزیت، تابلو، فرم و شبکه‌های اجتماعی الگوی مشترکی نداشتند.",
        },
      ],
      strategy: [
        {
          problem: "برند در نقاط تماس یکی نیست.",
          decision: "ساخت سیستم برند، نه فقط لوگو.",
          solution: "راهنمای برند شامل رنگ، تایپ، شبکه و کاربردها.",
        },
        {
          problem: "توسعه‌ی مواد جدید سلیقه‌ای است.",
          decision: "تعریف الگوی نقاط تماس.",
          solution: "قالب‌های آماده برای کارت، فرم، تابلو و شبکه‌ی اجتماعی.",
        },
      ],
      execution: [
        {
          title: "سیستم هویت",
          body: "تعریف لوگو، رنگ، تایپوگرافی و شبکه‌ی طراحی.",
          kind: "identity",
        },
        {
          title: "کاربرد در نقاط تماس",
          body: "الگوی کارت ویزیت، تابلو، فرم پذیرش و شبکه‌های اجتماعی.",
          kind: "content",
        },
      ],
      results: {
        qualitative: [
          "زبان بصری واحد در همه‌ی نقاط تماس اجرا شد.",
          "توسعه‌ی مواد ارتباطی جدید بر اساس راهنمای برند، بدون تصمیم سلیقه‌ای انجام می‌شود.",
          "کلینیک ابزار روشنی برای گسترش شعبه‌های جدید در اختیار دارد.",
        ],
      },
      serviceLinks: [{ label: "طراحی هویت بصری", to: "/services/brand-identity" }],
    },
  },
];

export const ALL_PROJECTS: Project[] = [FEATURED, ...PROJECTS];

export function getProjectBySlug(slug: string): Project | undefined {
  return ALL_PROJECTS.find((p) => p.slug === slug);
}

export function getAdjacent(slug: string): { prev?: Project; next?: Project } {
  const i = ALL_PROJECTS.findIndex((p) => p.slug === slug);
  if (i === -1) return {};
  return {
    prev: i > 0 ? ALL_PROJECTS[i - 1] : undefined,
    next: i < ALL_PROJECTS.length - 1 ? ALL_PROJECTS[i + 1] : undefined,
  };
}

export function getRelated(slug: string, limit = 3): Project[] {
  const cur = getProjectBySlug(slug);
  if (!cur) return [];
  const same = ALL_PROJECTS.filter((p) => p.slug !== slug && p.category === cur.category);
  const rest = ALL_PROJECTS.filter((p) => p.slug !== slug && p.category !== cur.category);
  return [...same, ...rest].slice(0, limit);
}
